import { useDebounceFn } from '@vueuse/core'
import { useParams } from '@deepcloud/deep-ui-utils/router'
import { useDeepBoxPreviewStore } from '@/stores/deepbox/deepboxes/preview.ts'

export function usePreviewNavigation(
  preventKeyboardNavigation: MaybeRefOrGetter<boolean> = ref(false),
) {
  const deepBoxPreviewStore = useDeepBoxPreviewStore()

  const router = useRouter()

  const params = useParams<{
    node: string
  }>()

  onMounted(() => {
    // register keyup navigation
    document.addEventListener('keyup', navigate)
  })

  onUnmounted(() => {
    // remove keyup navigation
    document.removeEventListener('keyup', navigate)
  })

  const parentChildrenFiles = computed(
    () =>
      deepBoxPreviewStore.parentNodeChildren?.nodes?.filter(
        (node) => node.type === 'file',
      ) || [],
  )

  const totalFilesSize = computed(() => {
    // size counts ALL nodes (files + folders), so we can't use it directly.
    // If your API has a file-only count, use that. Otherwise use size as an approximation,
    // or keep parentChildrenFiles.length as the loaded count and size as the total.
    return (
      deepBoxPreviewStore.parentNodeChildren?.size ??
      parentChildrenFiles.value.length
    )
  })

  // The display index: if not found in loaded slice, return null so UI can show a spinner
  const currentFileIndex = computed(() =>
    parentChildrenFiles.value.findIndex(
      (node) => node.nodeId === params.value.node,
    ),
  )

  const nextItemRoute = computed(() => {
    return {
      name: 'node-preview',
      params: {
        node: parentChildrenFiles.value[currentFileIndex.value + 1]?.nodeId,
      },
    }
  })

  const previousItemRoute = computed(() => {
    return {
      name: 'node-preview',
      params: {
        node: parentChildrenFiles.value[currentFileIndex.value - 1]?.nodeId,
      },
    }
  })

  const pendingIndex = ref<number | null>(null)

  const displayIndex = computed(
    () => pendingIndex.value ?? currentFileIndex.value,
  )

  const debouncedPush = useDebounceFn(() => {
    const idx = pendingIndex.value
    if (idx === null) return
    const node = parentChildrenFiles.value[idx]
    if (!node) return
    router.push({ name: 'node-preview', params: { node: node.nodeId } })
    pendingIndex.value = null
  }, 300)

  function openNextFile() {
    const current = pendingIndex.value ?? currentFileIndex.value
    if (current !== -1 && current < parentChildrenFiles.value.length - 1) {
      pendingIndex.value = current + 1
      debouncedPush()
    }
  }

  function openPreviousFile() {
    const current = pendingIndex.value ?? currentFileIndex.value
    if (current > 0) {
      pendingIndex.value = current - 1
      debouncedPush()
    }
  }

  function navigate(event: KeyboardEvent) {
    if (toValue(preventKeyboardNavigation)) return
    event.stopPropagation()
    if (event.key === 'ArrowRight') {
      openNextFile()
    } else if (event.key === 'ArrowLeft') {
      openPreviousFile()
    }
  }

  return {
    parentChildrenFiles,
    currentFileIndex,
    displayIndex,
    totalFilesSize,
    nextItemRoute,
    previousItemRoute,
  }
}
